import type { Account, Workspace } from '@deskworks/shared-types';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  BookOpen,
  Bot,
  Check,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  Database,
  Gauge,
  Inbox,
  Layers,
  LayoutDashboard,
  ListTodo,
  LogOut,
  UserRound,
  Menu,
  Monitor,
  PanelsTopLeft,
  Moon,
  Palette,
  Plug,
  Plus,
  Settings,
  ConciergeBell,
  Sun,
  SquareStack,
  SquareTerminal,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { AccountBrandingDialog } from '@/components/account-branding';
import { CreateWorkspaceDialog } from '@/components/create-workspace-dialog';
import { LiveWorkspaces } from '@/components/live-workspaces';
import { ProfileDialog } from '@/components/profile-dialog';
import { EnvironmentBadge } from '@/components/environment-badge';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EmptyState, ErrorState } from '@/components/states';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api, ApiError, setToken } from '@/lib/api';
import { useBrandLook, useLook } from '@/lib/brand';
import { t } from '@/lib/i18n';
import { findAccount, useInbox, useMe, useWorkspace, useWorkspaceId } from '@/lib/hooks';
import { qk } from '@/lib/query-keys';
import { useRealtimeStatus, useRealtimeSubscription, type RealtimeStatus } from '@/lib/realtime';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { LAST_WORKSPACE_KEY, readLastWorkspace } from './index-redirect';

/** Layout for `/w/$workspaceId/...`. */
export function AppShell() {
  const workspaceId = useWorkspaceId();
  const workspace = useWorkspace(workspaceId);

  useRealtimeSubscription(workspaceId);

  useEffect(() => {
    if (!workspace.data) return;
    try {
      localStorage.setItem(LAST_WORKSPACE_KEY, workspace.data.id);
    } catch {
      /* ignore */
    }
  }, [workspace.data]);

  const notFound = workspace.error instanceof ApiError && (workspace.error.status === 404 || workspace.error.status === 403);

  return (
    <ShellFrame
      navWorkspaceId={workspaceId}
      routeWorkspaceId={workspaceId}
      accountId={workspace.data?.accountId}
      title={workspace.data?.name}
      blocked={
        notFound ? (
          <div className="mx-auto max-w-xl p-8">
            <ErrorState title={t('Workspace not found')} error={workspace.error} onRetry={() => void workspace.refetch()} />
            <Button asChild variant="link" className="mt-2 px-0">
              <Link to="/">{t('Go to start page')}</Link>
            </Button>
          </div>
        ) : null
      }
    />
  );
}

/**
 * Layout for account-level pages `/$account/...` (`$account` = slug or id).
 * The workspace navigation in the sidebar points at the last used (or first) workspace of that account.
 */
export function AccountShell() {
  const { account: param } = useParams({ from: '/_authed/$account' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  const workspaces = useQuery({
    queryKey: qk.accountWorkspaces(account?.id ?? ''),
    queryFn: () => api.accounts.workspaces(account!.id),
    enabled: !!account,
  });
  const last = readLastWorkspace();
  const navWorkspaceId = workspaces.data?.find((w) => w.id === last)?.id ?? workspaces.data?.[0]?.id ?? '';

  return (
    <ShellFrame
      navWorkspaceId={navWorkspaceId}
      routeWorkspaceId=""
      accountId={account?.id}
      title={account?.name}
      blocked={
        me.isError ? (
          <div className="mx-auto max-w-xl p-8">
            <ErrorState error={me.error} onRetry={() => void me.refetch()} />
          </div>
        ) : me.data && !account ? (
          <div className="mx-auto max-w-xl p-8">
            <EmptyState
              title={t('Account not found')}
              description={t('There’s no Account “{account}” that you have access to.', { account: param })}
              action={
                <Button asChild variant="outline">
                  <Link to="/">{t('Go to start page')}</Link>
                </Button>
              }
            />
          </div>
        ) : null
      }
    />
  );
}

function ShellFrame({
  navWorkspaceId,
  routeWorkspaceId,
  accountId,
  title,
  blocked,
}: {
  /** Workspace whose pages the sidebar navigation links to. */
  navWorkspaceId: string;
  /** Workspace of the current route ('' on account-level pages). */
  routeWorkspaceId: string;
  accountId: string | undefined;
  title: string | undefined;
  /** Rendered instead of the page (e.g. not found). */
  blocked: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setMobileOpen(false), [pathname]);
  // The account's colors and logo (docs/features/branding.md).
  const look = useBrandLook(useMe().data?.accounts.find((a) => a.id === accountId)?.branding);

  const sidebar = <SidebarContent navWorkspaceId={navWorkspaceId} routeWorkspaceId={routeWorkspaceId} accountId={accountId} />;

  return (
    <div className="flex h-full min-h-0">
      {/* Desktop sidebar */}
      <aside className="brand-sidebar hidden w-60 shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:flex md:flex-col">
        {sidebar}
      </aside>

      {/* Phone: the full navigation slides in from the left ("More" in the tab bar, or the menu button). */}
      <div className={cn('fixed inset-0 z-50 md:hidden', !mobileOpen && 'pointer-events-none')} aria-hidden={!mobileOpen}>
        <div
          className={cn('absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200', mobileOpen ? 'opacity-100' : 'opacity-0')}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className={cn(
            'brand-sidebar absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col border-r border-sidebar-border bg-sidebar pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] text-sidebar-foreground shadow-2xl transition-[translate,visibility] duration-250 ease-out',
            mobileOpen ? 'visible translate-x-0' : 'invisible -translate-x-full',
          )}
        >
          {sidebar}
        </aside>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="z-30 flex shrink-0 items-center gap-2.5 border-b bg-background/85 px-3 pt-[env(safe-area-inset-top)] backdrop-blur-md supports-[backdrop-filter]:bg-background/70 md:hidden">
          <div className="flex h-12 min-w-0 flex-1 items-center gap-2.5">
            <Button variant="ghost" size="icon-sm" onClick={() => setMobileOpen(true)} aria-label={t('Menu')} className="-ml-1">
              <Menu />
            </Button>
            <Logo className="size-6" src={look?.logoUrl} />
            <span className="min-w-0 flex-1 truncate text-[15px] font-semibold tracking-tight">{title ?? 'Deskworks'}</span>
            <FrontDeskShortcut accountId={accountId} />
            <InboxShortcut accountId={accountId} />
          </div>
        </header>
        <main
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] max-md:overflow-x-hidden md:pb-0"
          id="main-scroll"
        >
          {blocked ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

/** The phone's header: what needs the person, one tap away (the full navigation is in the menu). */
/** On a phone the Front Desk is one tap away (docs/features/front-desk.md). */
function FrontDeskShortcut({ accountId }: { accountId: string | undefined }) {
  const me = useMe();
  const account = me.data?.accounts.find((a) => a.id === accountId);
  if (!account) return null;
  return (
    <Button asChild variant="ghost" size="icon-sm">
      <Link to="/$account/front-desk" params={{ account: account.slug || account.id }} search={{}} aria-label={t('Front Desk')} activeProps={{ className: 'text-primary' }}>
        <ConciergeBell />
      </Link>
    </Button>
  );
}

function InboxShortcut({ accountId }: { accountId: string | undefined }) {
  const me = useMe();
  const account = me.data?.accounts.find((a) => a.id === accountId);
  const inbox = useInbox(account?.id);
  if (!account) return null;
  const total = inbox.data?.total ?? 0;
  return (
    <Button asChild variant="ghost" size="icon-sm" className="relative -mr-1">
      <Link to="/$account/inbox" params={{ account: account.slug || account.id }} aria-label={t('Inbox')}>
        <Inbox />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[17px] rounded-full bg-orange-500 px-1 text-center text-[10px] leading-[17px] font-semibold text-white tabular-nums ring-2 ring-background">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </Link>
    </Button>
  );
}

function SidebarContent({
  navWorkspaceId: workspaceId,
  routeWorkspaceId,
  accountId,
}: {
  navWorkspaceId: string;
  routeWorkspaceId: string;
  accountId: string | undefined;
}) {
  const me = useMe();
  const account = me.data?.accounts.find((a) => a.id === accountId);
  const inbox = useInbox(account?.id);
  const inboxTotal = inbox.data?.total ?? 0;
  const navWorkspace = useWorkspace(workspaceId);
  const overview = useQuery({
    queryKey: qk.overview(workspaceId),
    queryFn: () => api.workspaces.overview(workspaceId),
    enabled: !!workspaceId,
  });
  const attention = overview.data?.counts.needsAttention ?? 0;
  // Reports nobody has answered yet (docs/features/desk-reports.md).
  const reports = useQuery({
    queryKey: qk.reports(workspaceId),
    queryFn: () => api.reports.list(workspaceId),
    enabled: !!workspaceId,
  });
  const newReports = (reports.data ?? []).filter((r) => r.phase === 'feedback' && !r.answered).length;
  const p = { workspaceId };

  return (
    <>
      <div className="p-3">
        <WorkspaceSwitcher workspaceId={routeWorkspaceId} accountId={accountId} />
        <EnvironmentBadge className="mt-2 ml-1.5" />
      </div>
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-1">
        {account && (
          <>
            {/* The Front Desk: where people work with VICKI (docs/features/front-desk.md). */}
            <Link
              to="/$account/front-desk"
              params={{ account: account.slug || account.id }}
              className={NAV_LINK_CLASS}
              activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
            >
              <ConciergeBell />
              <span className="flex-1 truncate">{t('Front Desk')}</span>
            </Link>
            <LiveWorkspaces accountId={account.id} />
            <Link
              to="/$account/inbox"
              params={{ account: account.slug || account.id }}
              className={NAV_LINK_CLASS}
              activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
            >
              <Inbox />
              <span className="flex-1 truncate">{t('Inbox')}</span>
              {inboxTotal > 0 && (
                <span
                  className="rounded-full bg-orange-500/15 px-1.5 text-[11px] font-semibold text-orange-700 tabular-nums dark:text-orange-300"
                  title={t('Needs you')}
                >
                  {inboxTotal}
                </span>
              )}
            </Link>
            <Link
              to="/$account/desks"
              params={{ account: account.slug || account.id }}
              className={NAV_LINK_CLASS}
              activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
            >
              <Layers />
              <span className="flex-1 truncate">{t('All desks')}</span>
            </Link>
          </>
        )}
        {workspaceId && (
          <div className="mt-3 mb-1 truncate px-2.5 text-[11px] font-medium tracking-wide text-muted-foreground uppercase" title={t('Workspace')}>
            {navWorkspace.data?.name ?? t('Workspace')}
          </div>
        )}
        {workspaceId && (
        <>
        <NavLink to="/w/$workspaceId" params={p} exact icon={<LayoutDashboard />} badge={attention || undefined}>
          {t('Overview')}
        </NavLink>
        <NavLink to="/w/$workspaceId/desks" params={p} icon={<SquareStack />}>
          {t('Desks')}
        </NavLink>
        <NavLink to="/w/$workspaceId/work-items" params={p} icon={<ListTodo />}>
          {t('Work Items')}
        </NavLink>
        <NavLink to="/w/$workspaceId/reports" params={p} icon={<ClipboardList />} badge={newReports || undefined}>
          {t('Reports')}
        </NavLink>
        <NavLink to="/w/$workspaceId/views" params={p} icon={<PanelsTopLeft />}>
          {t('Views')}
        </NavLink>
        <MoreNav workspaceId={workspaceId}>
          <NavLink to="/w/$workspaceId/agents" params={p} icon={<Bot />}>
            {t('Agents')}
          </NavLink>
          <NavLink to="/w/$workspaceId/connections" params={p} icon={<Plug />}>
            {t('Connections')}
          </NavLink>
          <NavLink to="/w/$workspaceId/database" params={p} icon={<Database />}>
            {t('Database')}
          </NavLink>
          <NavLink to="/w/$workspaceId/skills" params={p} icon={<BookOpen />}>
            {t('Skills')}
          </NavLink>
          <NavLink to="/w/$workspaceId/activity" params={p} icon={<Activity />}>
            {t('Activity')}
          </NavLink>
          <NavLink to="/w/$workspaceId/sessions" params={p} icon={<SquareTerminal />}>
            {t('Sessions')}
          </NavLink>
          <NavLink to="/w/$workspaceId/usage" params={p} icon={<Gauge />}>
            {t('AI usage')}
          </NavLink>
        </MoreNav>
        <div className="my-2 h-px bg-sidebar-border" />
        <NavLink to="/w/$workspaceId/settings" params={p} icon={<Settings />}>
          {t('Settings')}
        </NavLink>
        </>
        )}
      </nav>
      <div className="flex flex-col gap-1 border-t border-sidebar-border p-3">
        <div className="flex items-center justify-between gap-2">
          <ConnectionIndicator />
          <ThemeToggle />
        </div>
        <UserMenu />
      </div>
    </>
  );
}

const MORE_NAV_PATHS = ['agents', 'connections', 'database', 'skills', 'activity', 'sessions', 'usage'];
const MORE_NAV_KEY = 'deskworks.nav.more';

/**
 * The pages for setting up and looking under the hood, folded away so the everyday ones stand out. Open while one of
 * them is shown; otherwise as the viewer left it.
 */
function MoreNav({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onMorePage = MORE_NAV_PATHS.some((seg) => pathname.startsWith(`/w/${workspaceId}/${seg}`));
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(MORE_NAV_KEY) === 'open';
    } catch {
      return false;
    }
  });
  const toggle = (next: boolean) => {
    setOpen(next);
    try {
      localStorage.setItem(MORE_NAV_KEY, next ? 'open' : 'closed');
    } catch {
      // not remembered, still applied
    }
  };
  const shown = open || onMorePage;
  return (
    <Collapsible open={shown} onOpenChange={toggle}>
      <CollapsibleTrigger
        disabled={onMorePage}
        className="group flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground disabled:cursor-default disabled:hover:bg-transparent [&_svg]:size-4"
      >
        <ChevronRight className={cn('transition-transform', shown && 'rotate-90')} />
        <span className="flex-1 text-left">{t('More')}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-0.5 pt-0.5">{children}</CollapsibleContent>
    </Collapsible>
  );
}

const NAV_LINK_CLASS =
  'group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-sm text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-foreground [&_svg]:size-4 [&_svg]:text-muted-foreground';
const NAV_LINK_ACTIVE_CLASS = 'bg-sidebar-accent !text-foreground font-medium [&_svg]:!text-primary';

type NavTo =
  | '/w/$workspaceId'
  | '/w/$workspaceId/start'
  | '/w/$workspaceId/desks'
  | '/w/$workspaceId/work-items'
  | '/w/$workspaceId/reports'
  | '/w/$workspaceId/agents'
  | '/w/$workspaceId/connections'
  | '/w/$workspaceId/database'
  | '/w/$workspaceId/views'
  | '/w/$workspaceId/skills'
  | '/w/$workspaceId/activity'
  | '/w/$workspaceId/sessions'
  | '/w/$workspaceId/usage'
  | '/w/$workspaceId/settings';

function NavLink({
  to,
  params,
  icon,
  children,
  exact,
  badge,
}: {
  to: NavTo;
  params: { workspaceId: string };
  icon: ReactNode;
  children: ReactNode;
  exact?: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      params={params}
      activeOptions={{ exact: !!exact, includeSearch: false }}
      className={NAV_LINK_CLASS}
      activeProps={{ className: NAV_LINK_ACTIVE_CLASS }}
    >
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {badge ? (
        <span
          className="rounded-full bg-orange-500/15 px-1.5 text-[11px] font-semibold text-orange-700 tabular-nums dark:text-orange-300"
          title={t('Needs your attention')}
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
}

function WorkspaceSwitcher({ workspaceId, accountId }: { workspaceId: string; accountId: string | undefined }) {
  const me = useMe();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [brandingFor, setBrandingFor] = useState<Account | null>(null);
  const accounts: Account[] = me.data?.accounts ?? [];
  const wsQueries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: qk.accountWorkspaces(a.id),
      queryFn: () => api.accounts.workspaces(a.id),
    })),
  });
  const byAccount: { account: Account; workspaces: Workspace[] }[] = accounts.map((a, i) => ({
    account: a,
    workspaces: wsQueries[i]?.data ?? [],
  }));
  const current = byAccount.flatMap((g) => g.workspaces).find((w) => w.id === workspaceId);
  const currentAccount = accounts.find((a) => a.id === (current?.accountId ?? accountId));
  const look = useLook(currentAccount?.branding);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg p-1.5 text-left outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/50">
          <Logo className="size-8" src={look?.logoUrl} />
          <div className="min-w-0 flex-1">
            {current ? (
              <>
                <div className="truncate text-[11px] leading-tight text-muted-foreground">{currentAccount?.name ?? t('Account')}</div>
                <div className="truncate text-sm leading-tight font-semibold text-foreground">{current.name}</div>
              </>
            ) : !workspaceId && currentAccount ? (
              <>
                <div className="truncate text-[11px] leading-tight text-muted-foreground">{t('Account')}</div>
                <div className="truncate text-sm leading-tight font-semibold text-foreground">{currentAccount.name}</div>
              </>
            ) : (
              <>
                <Skeleton className="mb-1 h-3 w-16" />
                <Skeleton className="h-4 w-24" />
              </>
            )}
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {byAccount.map(({ account, workspaces }, idx) => (
            <div key={account.id}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{account.name}</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => void navigate({ to: '/$account/desks', params: { account: account.slug || account.id } })}
              >
                <Layers />
                <span className="flex-1 truncate">{t('All desks')}</span>
                {!workspaceId && account.id === accountId && <Check className="text-primary" />}
              </DropdownMenuItem>
              {['owner', 'admin'].includes(account.role) && (
                <DropdownMenuItem onSelect={() => setBrandingFor(account)}>
                  <Palette />
                  <span className="flex-1 truncate">{t('Colors & logo')}</span>
                </DropdownMenuItem>
              )}
              {workspaces.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">{t('No workspaces yet')}</div>}
              {workspaces.map((w) => (
                <DropdownMenuItem
                  key={w.id}
                  onSelect={() => void navigate({ to: '/w/$workspaceId', params: { workspaceId: w.id } })}
                >
                  <span className="flex-1 truncate">{w.name}</span>
                  {w.id === workspaceId && <Check className="text-primary" />}
                </DropdownMenuItem>
              ))}
            </div>
          ))}
          {accounts.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
                <Plus /> {t('Create workspace')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {accounts.length > 0 && (
        <CreateWorkspaceDialog
          accounts={accounts}
          defaultAccountId={currentAccount?.id}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      )}
      {brandingFor && (
        <AccountBrandingDialog
          account={accounts.find((a) => a.id === brandingFor.id) ?? brandingFor}
          open={!!brandingFor}
          onOpenChange={(open) => !open && setBrandingFor(null)}
        />
      )}
    </>
  );
}

const REALTIME_LABEL: Record<RealtimeStatus, { label: string; hint: string; dot: string }> = {
  connected: { label: t('Live'), hint: t('Changes show up right away.'), dot: 'bg-emerald-500' },
  connecting: { label: t('Connecting …'), hint: t('Setting up the live connection.'), dot: 'bg-amber-500 animate-soft-pulse' },
  disconnected: {
    label: t('Offline'),
    hint: t('No live connection. Data updates when you reload the page.'),
    dot: 'bg-zinc-400',
  },
};

function ConnectionIndicator() {
  const status = useRealtimeStatus();
  const meta = REALTIME_LABEL[status];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex cursor-default items-center gap-2 px-1 text-xs text-muted-foreground" aria-live="polite">
          <span className={cn('size-2 rounded-full', meta.dot)} />
          {meta.label}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">{meta.hint}</TooltipContent>
    </Tooltip>
  );
}

function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const options = [
    { value: 'light' as const, icon: <Sun />, label: t('Light') },
    { value: 'dark' as const, icon: <Moon />, label: t('Dark') },
    { value: 'system' as const, icon: <Monitor />, label: t('System') },
  ];
  return (
    <div className="flex items-center rounded-md border border-sidebar-border p-0.5" role="radiogroup" aria-label={t('Color theme')}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={preference === o.value}
          title={o.label}
          onClick={() => setPreference(o.value)}
          className={cn(
            'flex size-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground [&_svg]:size-3.5',
            preference === o.value && 'bg-sidebar-accent text-foreground',
          )}
        >
          {o.icon}
        </button>
      ))}
    </div>
  );
}

function UserMenu() {
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = me.data?.user;
  const [profileOpen, setProfileOpen] = useState(false);
  const initials = (user?.name || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  const logout = () => {
    setToken(null);
    queryClient.clear();
    void navigate({ to: '/login', search: {} });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2 rounded-md p-1.5 text-left outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/50">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-foreground">{user?.name || '…'}</div>
          <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-56">
        <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setProfileOpen(true)} disabled={!user}>
          <UserRound /> {t('Profile')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={logout}>
          <LogOut /> {t('Sign out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
      {user && <ProfileDialog user={user} open={profileOpen} onOpenChange={setProfileOpen} />}
    </DropdownMenu>
  );
}
